import React, { useState } from 'react';
import { post } from 'aws-amplify/api';
import { getCurrentUser, signInWithRedirect, fetchAuthSession } from 'aws-amplify/auth';
import './Pricing.css';

interface PricingTier {
  id: string;
  name: string;
  price: number;
  priceId: string; // Stripe price ID
  features: string[];
  popular?: boolean;
  cta: string;
}

const PRICING_TIERS: PricingTier[] = [
  {
    id: 'hobbyist',
    name: 'Hobbyist',
    price: 9,
    priceId: process.env.REACT_APP_STRIPE_PRICE_HOBBYIST || 'price_hobbyist_900',
    cta: 'Start Hobbyist Plan',
    features: [
      '50 GB storage',
      'Unlimited photo uploads',
      'SFTP camera upload',
      'Basic photo management',
      'Search by EXIF data',
      'Multi-tier storage',
      'Email support',
      '$0.20/GB overage pricing',
    ],
  },
  {
    id: 'workflow',
    name: 'Workflow',
    price: 29,
    priceId: process.env.REACT_APP_STRIPE_PRICE_WORKFLOW || 'price_workflow_2900',
    popular: true,
    cta: 'Start Workflow Plan',
    features: [
      'Everything in Hobbyist',
      '200 GB storage',
      'Burst AI detection & culling',
      'AI quality scoring',
      'iOS mobile app',
      'Lightroom integration',
      'Statistical insights',
      'Keyboard shortcuts',
      'Priority processing',
      '$0.15/GB overage pricing',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 49,
    priceId: process.env.REACT_APP_STRIPE_PRICE_PRO || 'price_pro_4900',
    cta: 'Start Pro Plan',
    features: [
      'Everything in Workflow',
      '500 GB storage',
      'Advanced ML features',
      'Peak action detection',
      'Composition analysis',
      'API access',
      'Webhook integrations',
      'Phone support',
      'Advanced analytics',
      '$0.10/GB overage pricing',
    ],
  },
];

export const Pricing: React.FC = () => {
  const [loadingTier, setLoadingTier] = useState<string | null>(null);

  const handleSubscribe = async (tier: PricingTier) => {
    setLoadingTier(tier.id);

    try {
      // Check if user is authenticated
      try {
        await getCurrentUser();
      } catch (authError) {
        // User not authenticated - redirect to sign in
        alert('Please sign in to subscribe to a plan');
        await signInWithRedirect({ provider: 'Google' });
        return;
      }

      // Get auth session and token
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('No authentication token available');
      }

      const restOperation = post({
        apiName: 'PhotosynthesizeAPI',
        path: '/subscription/checkout',
        options: {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: {
            priceId: tier.priceId,
          },
        },
      });

      const { body } = await restOperation.response;
      const response = (await body.json()) as any;

      // Redirect to Stripe Checkout
      if (response.checkoutUrl) {
        window.location.href = response.checkoutUrl;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      alert('Failed to start checkout. Please try again.');
      setLoadingTier(null);
    }
  };

  return (
    <div className="pricing-page">
      {/* Header */}
      <div className="pricing-header">
        <h1>Choose Your Plan</h1>
        <p className="pricing-subtitle">
          Simple, transparent pricing for photographers. No hidden fees.
        </p>
      </div>

      {/* Pricing Cards */}
      <div className="pricing-grid">
        {PRICING_TIERS.map((tier) => (
          <div
            key={tier.id}
            className={`pricing-card ${tier.popular ? 'popular' : ''}`}
          >
            {tier.popular && <div className="popular-badge">Most Popular</div>}

            <div className="pricing-card-header">
              <h2 className="tier-name">{tier.name}</h2>
              <div className="tier-price">
                <span className="price-currency">$</span>
                <span className="price-amount">{tier.price}</span>
                <span className="price-period">/month</span>
              </div>
            </div>

            <ul className="feature-list">
              {tier.features.map((feature, index) => (
                <li key={index} className="feature-item">
                  <svg
                    className="feature-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <button
              className={`subscribe-btn ${tier.popular ? 'popular' : ''}`}
              onClick={() => handleSubscribe(tier)}
              disabled={loadingTier === tier.id}
            >
              {loadingTier === tier.id ? 'Processing...' : tier.cta}
            </button>
          </div>
        ))}
      </div>

      {/* Free Trial */}
      <div className="free-tier-section">
        <div className="free-tier-card">
          <div className="free-tier-content">
            <h3>7-Day Free Trial</h3>
            <p>Try Photosynthesize risk-free for one week</p>
            <ul className="free-features">
              <li>50 GB storage (same as Hobbyist)</li>
              <li>All basic features included</li>
              <li>7-day full access</li>
              <li>90-day grace period after trial</li>
            </ul>
            <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#9ca3af' }}>
              Subscribe anytime to keep your photos permanently
            </p>
          </div>
          <div className="free-tier-price">
            <span className="free-price">$0</span>
            <span className="free-period">for 7 days</span>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="faq-section">
        <h2>Frequently Asked Questions</h2>

        <div className="faq-grid">
          <div className="faq-item">
            <h3>Can I change plans later?</h3>
            <p>
              Yes! You can upgrade or downgrade your plan at any time. Changes
              take effect at the start of your next billing cycle.
            </p>
          </div>

          <div className="faq-item">
            <h3>What payment methods do you accept?</h3>
            <p>
              We accept all major credit cards (Visa, Mastercard, American
              Express) through our secure payment processor, Stripe.
            </p>
          </div>

          <div className="faq-item">
            <h3>Is there a free trial?</h3>
            <p>
              Yes! All new users get a 7-day free trial with 50 GB of storage.
              After 7 days, subscribe to keep your photos permanently, or they'll
              be scheduled for deletion after a 90-day grace period.
            </p>
          </div>

          <div className="faq-item">
            <h3>What happens if I cancel?</h3>
            <p>
              You'll continue to have access to your paid plan until the end of
              your billing period. Your photos will remain stored as long as you
              stay within your plan's storage limit.
            </p>
          </div>

          <div className="faq-item">
            <h3>Do you offer refunds?</h3>
            <p>
              We offer a 14-day money-back guarantee. If you're not satisfied,
              contact us for a full refund.
            </p>
          </div>

          <div className="faq-item">
            <h3>How is storage calculated?</h3>
            <p>
              Storage is calculated based on the total size of your original
              photos across all time. Thumbnails and metadata don't count toward
              your limit. If you exceed your plan's storage, we charge $0.10-$0.20
              per GB depending on your plan.
            </p>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="pricing-footer">
        <h2>Still have questions?</h2>
        <p>
          Contact our support team at{' '}
          <a href="mailto:support@photosynthesize.app">
            support@photosynthesize.app
          </a>
        </p>
      </div>
    </div>
  );
};

export default Pricing;
